import { useEffect, useState } from 'react';
import { Plus, Trash2, Eye, EyeOff } from 'lucide-react';
import { Modal } from '../../components/Modal';
import { ScheduleGrid } from '../../components/ScheduleGrid';
import { supabase } from '../supabase';
import {
  EMPTY_SCHEDULE_GRID,
  normalizeScheduleGrid,
  type ScheduleEvent,
  type ScheduleGridData,
} from '../../types/schedule';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  program: { id: string; name: string } | null;
  /** Called after a successful save so the parent can refresh if needed. */
  onSaved?: () => void;
}

interface EditorState {
  themeEn: string;
  themeZh: string;
  notes: string;
  grid: ScheduleGridData;
}

const BLANK: EditorState = {
  themeEn: '',
  themeZh: '',
  notes: '',
  grid: EMPTY_SCHEDULE_GRID,
};

const NEW_EVENT: ScheduleEvent = { start: '09:00', end: '09:30', activity: '' };

/**
 * Admin editor for a program's daily activity timetable. Edits theme/notes and
 * a per-day list of timed events (start/end/activity), with a live read-only
 * calendar preview. Upserts the single `program_schedules` row for the program.
 */
export function CampScheduleEditor({ isOpen, onClose, program, onSaved }: Props) {
  const [state, setState] = useState<EditorState>(BLANK);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (!isOpen || !program) return;
    let active = true;
    setLoading(true);
    setError(null);
    setShowPreview(false);
    (async () => {
      const { data, error: err } = await supabase
        .from('program_schedules')
        .select('theme_en, theme_zh, notes, schedule')
        .eq('program_id', program.id)
        .maybeSingle();
      if (!active) return;
      if (err) {
        setError(err.message);
        setState(BLANK);
      } else if (data) {
        const row = data as unknown as {
          theme_en: string | null; theme_zh: string | null;
          notes: string | null; schedule: ScheduleGridData;
        };
        const grid = normalizeScheduleGrid(row.schedule);
        setState({
          themeEn: row.theme_en ?? '',
          themeZh: row.theme_zh ?? '',
          notes: row.notes ?? '',
          grid: grid.days.length > 0 ? grid : EMPTY_SCHEDULE_GRID,
        });
      } else {
        setState(BLANK);
      }
      setLoading(false);
    })();
    return () => { active = false; };
  }, [isOpen, program]);

  function updateGrid(mut: (g: ScheduleGridData) => ScheduleGridData) {
    setState(s => ({ ...s, grid: mut(s.grid) }));
  }

  function updateDay(dayIdx: number, mut: (d: ScheduleGridData['days'][number]) => ScheduleGridData['days'][number]) {
    updateGrid(g => ({ ...g, days: g.days.map((d, i) => (i === dayIdx ? mut(d) : d)) }));
  }

  function setDayLabel(dayIdx: number, value: string) {
    updateDay(dayIdx, d => ({ ...d, label: value }));
  }

  function addDay() {
    updateGrid(g => ({ ...g, days: [...g.days, { label: '', events: [] }] }));
  }

  function removeDay(dayIdx: number) {
    updateGrid(g => ({ ...g, days: g.days.filter((_, i) => i !== dayIdx) }));
  }

  function addEvent(dayIdx: number) {
    updateDay(dayIdx, d => ({ ...d, events: [...d.events, { ...NEW_EVENT }] }));
  }

  function removeEvent(dayIdx: number, evIdx: number) {
    updateDay(dayIdx, d => ({ ...d, events: d.events.filter((_, i) => i !== evIdx) }));
  }

  function setEvent(dayIdx: number, evIdx: number, field: keyof ScheduleEvent, value: string) {
    updateDay(dayIdx, d => ({
      ...d,
      events: d.events.map((e, i) => (i === evIdx ? { ...e, [field]: value } : e)),
    }));
  }

  async function handleSave() {
    if (!program) return;
    setSaving(true);
    setError(null);
    // Drop events with no activity; keep day columns even if empty.
    const cleaned: ScheduleGridData = {
      days: state.grid.days.map(d => ({
        label: d.label,
        events: d.events.filter(e => e.activity.trim()),
      })),
    };
    const { error: err } = await supabase
      .from('program_schedules')
      .upsert(
        {
          program_id: program.id,
          theme_en: state.themeEn.trim() || null,
          theme_zh: state.themeZh.trim() || null,
          notes: state.notes.trim() || null,
          schedule: cleaned,
        },
        { onConflict: 'program_id' },
      );
    setSaving(false);
    if (err) {
      setError(err.message);
      return;
    }
    onSaved?.();
    onClose();
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={program ? `Schedule — ${program.name}` : 'Schedule'}
    >
      {loading ? (
        <div className="space-y-3">
          <div className="h-10 bg-gray-100 animate-pulse rounded" />
          <div className="h-48 bg-gray-100 animate-pulse rounded" />
        </div>
      ) : (
        <div className="space-y-4">
          {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</p>}

          {/* Theme + notes */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="text-sm">
              <span className="block text-gray-600 mb-1">Theme (English)</span>
              <input
                value={state.themeEn}
                onChange={e => setState(s => ({ ...s, themeEn: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Ocean Explorers"
              />
            </label>
            <label className="text-sm">
              <span className="block text-gray-600 mb-1">Theme (中文)</span>
              <input
                value={state.themeZh}
                onChange={e => setState(s => ({ ...s, themeZh: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="海洋探险家"
              />
            </label>
          </div>
          <label className="text-sm block">
            <span className="block text-gray-600 mb-1">Notes (optional)</span>
            <input
              value={state.notes}
              onChange={e => setState(s => ({ ...s, notes: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Drop-off: 8:30 AM | Dismissal: 6:00 PM"
            />
          </label>

          {/* Preview toggle */}
          <button
            type="button"
            onClick={() => setShowPreview(v => !v)}
            className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800"
          >
            {showPreview ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            {showPreview ? 'Hide preview' : 'Show preview'}
          </button>
          {showPreview && <ScheduleGrid data={state.grid} highlightToday={false} />}

          {/* Per-day event sections */}
          <div className="space-y-3">
            {state.grid.days.map((day, d) => (
              <div key={d} className="border border-gray-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <input
                    value={day.label}
                    onChange={e => setDayLabel(d, e.target.value)}
                    className="flex-1 px-2 py-1.5 border border-gray-200 rounded text-sm font-medium focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="Monday 周一"
                  />
                  <button
                    type="button"
                    onClick={() => removeDay(d)}
                    className="text-gray-300 hover:text-red-500 flex-shrink-0"
                    aria-label="Remove day"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>

                {day.events.length > 0 && (
                  <div className="space-y-2">
                    {day.events.map((event, e) => (
                      <div key={e} className="flex items-start gap-1.5">
                        <input
                          type="time"
                          value={event.start}
                          onChange={ev => setEvent(d, e, 'start', ev.target.value)}
                          className="w-24 px-1.5 py-1 border border-gray-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                          aria-label="Start time"
                        />
                        <span className="text-gray-400 text-xs pt-1.5">–</span>
                        <input
                          type="time"
                          value={event.end}
                          onChange={ev => setEvent(d, e, 'end', ev.target.value)}
                          className="w-24 px-1.5 py-1 border border-gray-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                          aria-label="End time"
                        />
                        <textarea
                          value={event.activity}
                          onChange={ev => setEvent(d, e, 'activity', ev.target.value)}
                          rows={1}
                          className="flex-1 px-2 py-1 border border-gray-200 rounded text-xs resize-y focus:outline-none focus:ring-1 focus:ring-blue-500"
                          placeholder="Check in 签到"
                        />
                        <button
                          type="button"
                          onClick={() => removeEvent(d, e)}
                          className="text-gray-300 hover:text-red-500 flex-shrink-0 pt-1.5"
                          aria-label="Remove event"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => addEvent(d)}
                  className="mt-2 inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                >
                  <Plus className="h-3.5 w-3.5" /> Add event
                </button>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={addDay}
            className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
          >
            <Plus className="h-4 w-4" /> Add day
          </button>

          {/* Footer */}
          <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className={`px-4 py-2 rounded-md text-white ${saving ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'} transition-colors`}
            >
              {saving ? 'Saving…' : 'Save Schedule'}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
